import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import './contact-info-button.scss';

interface ContactInfo {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  company_name: string | null;
}

interface ContactInfoButtonProps {
  contactId: string | number | null | undefined;
}

const ContactInfoButton: React.FC<ContactInfoButtonProps> = ({ contactId }) => {
  const supabase = useSupabaseClient();
  const [isOpen, setIsOpen] = useState(false);
  const [contact, setContact] = useState<ContactInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const fetchContact = useCallback(async () => {
    if (!contactId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, phone, client_id, clients!inner(company_name)')
        .eq('id', contactId)
        .single();

      if (error) {
        console.error('Error fetching contact:', error);
        return;
      }

      const clientData = data.clients as unknown as { company_name: string } | null;
      setContact({
        id: data.id,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        company_name: clientData?.company_name || null
      });
    } catch (err) {
      console.error('Error fetching contact info:', err);
    } finally {
      setLoading(false);
    }
  }, [contactId, supabase]);

  const handleToggle = useCallback(() => {
    if (!isOpen) {
      fetchContact();
    }
    setIsOpen(prev => !prev);
  }, [isOpen, fetchContact]);

  const handleCopy = useCallback(async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(field);
      setTimeout(() => setCopied(null), 1500);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const hasContact = !!contactId;

  return (
    <div className="contact-info-button-wrapper">
      <button
        ref={buttonRef}
        type="button"
        className={`contact-info-btn${!hasContact ? ' disabled' : ''}`}
        onClick={hasContact ? handleToggle : undefined}
        disabled={!hasContact}
        title={hasContact ? "View contact info" : "Select a contact first"}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="2" width="14" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
          <circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1.2" />
          <path d="M3.5 12.5C3.5 10.5 5 9.5 7 9.5C9 9.5 10.5 10.5 10.5 12.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          <line x1="12" y1="5.5" x2="13.5" y2="5.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          <line x1="12" y1="8" x2="13.5" y2="8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </button>

      {isOpen && (
        <div ref={popoverRef} className="contact-info-popover">
          {loading ? (
            <div className="contact-info-loading">Loading...</div>
          ) : contact ? (
            <>
              <div className="contact-info-header">
                <span className="contact-info-name">
                  {contact.first_name} {contact.last_name}
                </span>
                {contact.company_name && (
                  <span className="contact-info-company">{contact.company_name}</span>
                )}
              </div>
              <div className="contact-info-fields">
                {contact.email ? (
                  <div className="contact-info-row">
                    <span className="contact-info-label">Email</span>
                    <div className="contact-info-value-row">
                      <a href={`mailto:${contact.email}`} className="contact-info-value">
                        {contact.email}
                      </a>
                      <button
                        type="button"
                        className="copy-btn"
                        onClick={() => handleCopy(contact.email!, 'email')}
                        title="Copy email"
                      >
                        {copied === 'email' ? '✓' : (
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="4" y="4" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1.2" />
                            <path d="M10 4V2.5C10 1.95 9.55 1.5 9 1.5H2.5C1.95 1.5 1.5 1.95 1.5 2.5V9C1.5 9.55 1.95 10 2.5 10H4" stroke="currentColor" strokeWidth="1.2" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="contact-info-row">
                    <span className="contact-info-label">Email</span>
                    <span className="contact-info-empty">Not provided</span>
                  </div>
                )}
                {contact.phone ? (
                  <div className="contact-info-row">
                    <span className="contact-info-label">Phone</span>
                    <div className="contact-info-value-row">
                      <a href={`tel:${contact.phone}`} className="contact-info-value">
                        {contact.phone}
                      </a>
                      <button
                        type="button"
                        className="copy-btn"
                        onClick={() => handleCopy(contact.phone!, 'phone')}
                        title="Copy phone"
                      >
                        {copied === 'phone' ? '✓' : (
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="4" y="4" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1.2" />
                            <path d="M10 4V2.5C10 1.95 9.55 1.5 9 1.5H2.5C1.95 1.5 1.5 1.95 1.5 2.5V9C1.5 9.55 1.95 10 2.5 10H4" stroke="currentColor" strokeWidth="1.2" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="contact-info-row">
                    <span className="contact-info-label">Phone</span>
                    <span className="contact-info-empty">Not provided</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="contact-info-loading">Contact not found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactInfoButton;
